import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, FormControl, Grid, InputLabel, MenuItem, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { complianceApi, ComplianceViolation, ComplianceViolationType } from 'api';
import { useSeason } from 'contexts/SeasonContext';

interface Props { open: boolean; onClose: () => void; }

const TYPE_COLORS: Record<ComplianceViolationType, 'error' | 'warning' | 'info'> = {
  DAILY_REST: 'error',
  WEEKLY_REST: 'error',
  DAILY_MAX: 'error',
  WEEKLY_MAX: 'warning',
  BREAK_MISSING: 'warning',
  ABSENCE_OVERLAP: 'error',
};

const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); };
const lastOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0); };
const toInput = (d: Date) => format(d, 'yyyy-MM-dd');

const ComplianceOverviewModal: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { seasonId } = useSeason();
  const [from, setFrom] = useState(toInput(firstOfMonth()));
  const [to, setTo] = useState(toInput(lastOfMonth()));
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<string>('');
  const [violations, setViolations] = useState<ComplianceViolation[] | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');

  const run = async () => {
    setLoading(true);
    try {
      const res = await complianceApi.getViolations({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        seasonId,
      });
      setMode(res.data.mode);
      setViolations(res.data.violations ?? []);
    } catch {
      setViolations([]);
    } finally {
      setLoading(false);
    }
  };

  const shown = (violations ?? []).filter((v) => typeFilter === 'ALL' || v.type === typeFilter);
  const types: ComplianceViolationType[] = ['DAILY_REST', 'WEEKLY_REST', 'DAILY_MAX', 'WEEKLY_MAX', 'BREAK_MISSING', 'ABSENCE_OVERLAP'];

  // Build the localized detail message from the violation's structured meta.
  // Falls back to the backend-provided English `detail` if meta is missing.
  const detailText = (v: ComplianceViolation): string => {
    const m = v.meta ?? {};
    switch (v.type) {
      case 'DAILY_MAX':
        return t('compliance.detail_DAILY_MAX', { hours: m.hours ?? ((m.minutes ?? 0) / 60).toFixed(1), max: m.maxDailyHours });
      case 'DAILY_REST':
        return t('compliance.detail_DAILY_REST', { hours: m.gapHours, min: m.minDailyRestHours });
      case 'BREAK_MISSING':
        return t('compliance.detail_BREAK_MISSING', { hours: m.breakAfterHours, min: m.minBreakMinutes });
      case 'WEEKLY_MAX':
        return t('compliance.detail_WEEKLY_MAX', { hours: m.hours ?? ((m.minutes ?? 0) / 60).toFixed(1), week: m.week, max: m.weeklyMaxAvgHours });
      case 'ABSENCE_OVERLAP':
        return t('compliance.detail_ABSENCE_OVERLAP', { type: m.absenceType });
      default:
        return v.detail;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('compliance.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('compliance.subtitle')}
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="date" label={t('compliance.from')}
              InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="date" label={t('compliance.to')}
              InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('compliance.type')}</InputLabel>
              <Select label={t('compliance.type')} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="ALL">{t('compliance.allTypes')}</MenuItem>
                {types.map((ty) => <MenuItem key={ty} value={ty}>{t(`compliance.type_${ty}`)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Button fullWidth variant="contained" onClick={run} disabled={loading}>
              {t('compliance.run')}
            </Button>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />

        {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

        {!loading && violations === null && (
          <Typography variant="body2" color="text.secondary">{t('compliance.runHint')}</Typography>
        )}

        {!loading && mode === 'off' && (
          <Typography variant="body2" color="warning.main">{t('compliance.disabled')}</Typography>
        )}

        {!loading && violations !== null && mode !== 'off' && shown.length === 0 && (
          <Typography variant="body2" color="success.main">✅ {t('compliance.clear')}</Typography>
        )}

        {!loading && shown.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('compliance.date')}</TableCell>
                  <TableCell>{t('compliance.employee')}</TableCell>
                  <TableCell>{t('compliance.type')}</TableCell>
                  <TableCell>{t('compliance.detail')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((v, i) => (
                  <TableRow key={i}>
                    <TableCell>{format(new Date(v.date), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{v.employeeName}</TableCell>
                    <TableCell>
                      <Chip size="small" color={TYPE_COLORS[v.type]} label={t(`compliance.type_${v.type}`)} />
                    </TableCell>
                    <TableCell>{detailText(v)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComplianceOverviewModal;
